import {
  differenceInCalendarDays,
  format,
  formatDistanceToNow,
  isAfter,
  isBefore,
  isEqual,
  isValid,
  isWithinInterval,
  subDays,
} from "date-fns";
import { is } from "src/util/is";

// ----------------------------------------------------------------------

export function fDate(date) {
  return format(new Date(date), "dd MMMM yyyy");
}

export function fDateTime(date, dateFormat = "dd MMM yyyy HH:mm") {
  const parsedDate = new Date(date);
  return !is.empty(date) && isValid(parsedDate)
    ? format(parsedDate, dateFormat)
    : "";
}

export function fDateTimeSuffix(date) {
  return format(new Date(date), "dd/MM/yyyy hh:mm p");
}

export function f(date, _format) {
  return format(new Date(date), _format);
}

export function fLocalTime(date) {
  return !is.empty(date) && isValid(new Date(date))
    ? format(new Date(date), "MMMM dd, yyyy, hh:mm:ss")
    : "";
}

export function fToNow(date) {
  return formatDistanceToNow(new Date(date), {
    addSuffix: true,
  });
}
export const onlyTime = (date) => {
  return !is.empty(date) && isValid(new Date(date))
    ? format(new Date(date), "hh:mm:ss a")
    : "";
};

export const yearFirstDateTimeFormat = (date) => {
  return !is.empty(date) && isValid(new Date(date))
    ? format(new Date(date), "yyyy-MM-dd hh:mm:ss a")
    : "";
};

export const yearFirstDateTimeFormat2 = (date) => {
  if (!is.empty(date) && isValid(new Date(date))) {
    const utcDate = new Date(date);
    // Get local timezone offset in minutes
    const timezoneOffset = utcDate.getTimezoneOffset();
    // Convert to local time by subtracting the offset
    const localTime = new Date(utcDate.getTime() - (timezoneOffset * 60000));
    const formatted = format(localTime, "yyyy-MM-dd hh:mm:ss a");
    // Get timezone name
    const tzAbbr = new Date().toLocaleString('en', {timeZoneName:'short'}).split(' ').pop();
    return `${formatted}`;
  }
  return "";
}

export const changeToISODateTimeString = (data) => {
  let date = data;
  if (typeof date === "object") {
    date = date.toISOString();
  } else {
    date = new Date(date).toISOString();
  }
  const withoutUTCZulu = date.replace("Z", "+00:00");
  return withoutUTCZulu;
};

export const remoteOperationsDateFormat = (data) => {
  let date = data;
  if (typeof date === "object") {
    date = date.toISOString();
  } else {
    date = new Date(date).toISOString();
  }
  return !is.empty(date) && isValid(new Date(date))
    ? format(new Date(date), "yyyy-MM-dd HH:mm")
    : "";
};
export const withinTimeBounds = (
  dateTimeToCompare,
  dateTimeToCompareAgainst,
  limitToConsider
) => {
  const upperLimitBound = new Date(dateTimeToCompareAgainst);
  const lowerTimeBound = new Date(
    upperLimitBound.getTime() - limitToConsider * 60000
  );

  const isWithinBounds = isWithinInterval(dateTimeToCompare, {
    start: lowerTimeBound,
    end: upperLimitBound,
  });
  return isWithinBounds;
};

export const giveTimeAndUTCTimeOffset = (
  timeToCalculateAgainst = new Date()
) => {
  const timezoneOffset = format(timeToCalculateAgainst, "xxx"); //OFFSET IN FORMAT '+/- hh:mm'
  const offsetInHrs = timezoneOffset.toString().split(/-|\+/gm)[1]; // ARRAY OF OFFSET STRING ['+||-',"hh:mm"]
  const offsetDirection =
    offsetInHrs.split(":")[0] === "00"
      ? "equal"
      : timezoneOffset[0] === "+"
        ? "ahead"
        : "behind";
  return offsetDirection !== "equal" ? (
    <p style={{ color: "red" }}>
      Your current timezone is {offsetInHrs}hrs {offsetDirection}.
    </p>
  ) : (
    <p style={{ color: "green" }}>
      Your current timezone is in accordance with UTC.
    </p>
  );
};

// OCPI Session Utility Functions
export const formatOcpiDate = (dateString, formatString = 'MMM dd, yyyy HH:mm:ss') => {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);
    return format(date, formatString);
  } catch (error) {
    console.warn('Invalid date string:', dateString);
    return dateString;
  }
};

export const calculateOcpiDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return 'N/A';

  try {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end - start;

    if (durationMs < 0) return 'Invalid duration';

    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((durationMs % (1000 * 60)) / 1000);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  } catch (error) {
    console.warn('Error calculating duration:', error);
    return 'N/A';
  }
};

export const formatOcpiRelativeTime = (dateString) => {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    console.warn('Error formatting relative time:', error);
    return dateString;
  }
};

export const formatOcpiCurrency = (amount, currency = 'INR') => {
  if (amount === null || amount === undefined) return 'N/A';

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  } catch (error) {
    console.warn('Error formatting currency:', error);
    return `${currency} ${amount}`;
  }
};

export const formatOcpiEnergy = (kwh) => {
  if (kwh === null || kwh === undefined) return '0 kWh';

  try {
    const formatted = parseFloat(kwh).toFixed(4);
    return `${formatted} kWh`;
  } catch (error) {
    console.warn('Error formatting energy:', error);
    return `${kwh} kWh`;
  }
};

export const isOcpiSessionActive = (session) => {
  if (!session || !session.startTime) return false;

  try {
    const startTime = new Date(session.startTime);
    const now = new Date();

    // Session is active if it started within the last 24 hours and has no end time
    const isRecent = (now - startTime) < (24 * 60 * 60 * 1000);
    const hasNoEndTime = !session.endTime;

    return isRecent && hasNoEndTime;
  } catch (error) {
    console.warn('Error checking session status:', error);
    return false;
  }
};

export const dashboardDateFormatter = (date, dateFormat = "yyyy-MM-dd") => {
  return format(date, dateFormat);
};

export const timeIntervalFormatter = (endDate, intervalValue) => {
  const startDate = subDays(endDate, intervalValue);
  return {
    start: dashboardDateFormatter(startDate),
    end: dashboardDateFormatter(endDate),
  };
};

export const isValidTimeInterval = ({ start, end }) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const today = new Date();
  const isStartBeforeOrEqualToEnd =
    isBefore(startDate, endDate) || isEqual(startDate, endDate);
  const bothNotAfterCurrent =
    isAfter(startDate, today) && isAfter(endDate, today);
  return isStartBeforeOrEqualToEnd && !bothNotAfterCurrent;
};

export const differenceBetween = ({ start, end }) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const difference = differenceInCalendarDays(endDate, startDate);
  return difference;
};

export const calculateBaseDateRange = ({ start, end, type }) => {
  let dateGap = differenceBetween({ start, end });
  let previousDay = subDays(new Date(start), 1);
  const baseDateRange = timeIntervalFormatter(previousDay, dateGap);

  return {
    start,
    end,
    type,
    baseStart: baseDateRange.start,
    baseEnd: baseDateRange.end,
  };
};

export const addTimeToDateString = (
  dateString,
  time = format(new Date(), "HH:mm:ss")
) => {
  return dateString;
};
